import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useScriptCreation, ZenPartOptions } from '../../contexts/ScriptCreationContext';
import { meditationParts, MeditationPart } from '../../contexts/ScriptCreationContext';
import { HelpButton } from '../../components/common/HelpButton';
import { getZenPartRange } from '../../config/zenPartOptions';

const helpContent = `
<div class="alert info">
   Select the parts that you wish to include in your meditation.
   </br></br>
<ul>
<li>Part names are suggestions to help you in planning your meditation, and to help AI determine how to help write the script (if you use AI).</li>
<li>Meditation parts will be written in the order you select them, but can be reordered in the next steps.</li>
<li>You must select at least one part.</li>
<li>If you want to create an unguided meditation, select only the "Self Guided / No Speaking" part.</li>
<li>Press the "Next" button to continue to the next step.</li>
</div>

`;

const Container = styled.div`
  //max-width: 1200px;
  margin: 0 auto;
`;

const Subtitle = styled.p`
  color: #cccccc;
  margin-bottom: 30px;
  font-size: 16px;
`;

const PartsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
`;

const PartCard = styled.div<{ selected: boolean }>`
  background-color: ${props => props.selected ? '#2a4a7a' : '#2a2a2a'};
  border: 2px solid ${props => props.selected ? '#007bff' : '#444'};
  border-radius: 12px;
  padding: 20px;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    border-color: #007bff;
    background-color: ${props => props.selected ? '#2a4a7a' : '#333'};
  }
`;

const PartTitle = styled.h3`
  color: #ffffff;
  margin: 0 0 10px 0;
  font-size: 18px;
`;

const PartDescription = styled.p`
  color: #cccccc;
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
`;

const SelectedCount = styled.div`
  text-align: center;
  color: #ffffff;
  font-size: 18px;
  font-weight: 600;
  margin-top: 20px;
`;

const NextButton = styled.button`
  background-color: #007bff;
  color: white;
  border: none;
  padding: 15px 30px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s;
  margin-top: 20px;

  &:hover {
    background-color: #0056b3;
  }

  &:disabled {
    background-color: #666;
    cursor: not-allowed;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 15px;
  margin-top: 20px;
`;

const ZenButton = styled(NextButton)`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);

  &:hover:not(:disabled) {
    background: linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%);
  }

  &:disabled {
    background: #666;
  }
`;



// Using centralized meditationParts from ScriptCreationContext

const Plan: React.FC = () => {
  const { selectedParts, togglePart, saveScript, zenSettings, setZenSettings, setSegmentationMode } = useScriptCreation();
  const navigate = useNavigate();

  const isOnlySelfGuided = selectedParts.length === 1 && selectedParts.includes('selfguided');

  // Deferred continue: runs after a setZenSettings(null) has committed, so
  // saveScript persists the cleared zen_settings instead of a stale closure value.
  const [pendingManualNext, setPendingManualNext] = useState<null | 'selfguided' | 'write'>(null);
  useEffect(() => {
    if (!pendingManualNext) return;
    const destination = pendingManualNext;
    setPendingManualNext(null);
    (async () => {
      if (destination === 'selfguided') {
        try {
          await saveScript();
        } catch (error) {
          console.error('Error saving script:', error);
        }
        window.scrollTo({ top: 0, behavior: 'smooth' });
        navigate('../audio');
      } else {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        navigate('../write');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingManualNext]);

  const handleNext = () => {
    // Leaving Zen Mode if it was previously initialized
    if (zenSettings) {
      setZenSettings(null);
    }
    setPendingManualNext(isOnlySelfGuided ? 'selfguided' : 'write');
  };

  const handleZenMode = () => {
    // Initialize zen settings with per-part defaults (preserve any existing choices)
    const partOptions: Record<string, ZenPartOptions> = {};
    selectedParts.forEach(partId => {
      const existing = zenSettings?.partOptions?.[partId];
      const range = getZenPartRange(partId);
      partOptions[partId] = existing || {
        minMinutes: range.min,
        maxMinutes: range.max,
        popularOptions: [],
        customInstructions: ''
      };
    });

    setZenSettings({
      enabled: true,
      goal: zenSettings?.goal || '',
      ambience: zenSettings?.ambience || 'auto',
      ambienceOther: zenSettings?.ambienceOther,
      music: zenSettings?.music || 'auto',
      musicOther: zenSettings?.musicOther,
      brainwave: zenSettings?.brainwave || 'auto',
      whiteNoise: zenSettings?.whiteNoise || 'auto',
      partOptions,
      stage: zenSettings?.stage || 'options'
    });
    // Zen speech is always segmented by sentence
    setSegmentationMode('sentence');
    window.scrollTo({ top: 0, behavior: 'smooth' });
    navigate('../zen');
  };

  return (
    <Container>
      <Subtitle>Choose the parts that you wish to include in your meditation:</Subtitle>
      
      <PartsGrid>
        {meditationParts.map(part => (
          <PartCard
            key={part.id}
            selected={selectedParts.includes(part.id)}
            onClick={() => togglePart(part.id)}
          >
            <PartTitle>{part.title}</PartTitle>
            <PartDescription>{part.description}</PartDescription>
          </PartCard>
        ))}
      </PartsGrid>
      
      <SelectedCount>
        {selectedParts.length === 0 
          ? 'Select parts to continue to the next step'
          : `${selectedParts.length} part${selectedParts.length === 1 ? '' : 's'} selected`
        }
      </SelectedCount>

      <ButtonContainer>
        <ZenButton
          onClick={handleZenMode}
          disabled={selectedParts.length === 0 || isOnlySelfGuided}
          title={isOnlySelfGuided ? 'Unguided meditations use the manual flow' : 'Let AI write and mix your meditation'}
        >
          ✨ Continue — Intelligent Zen Mode
        </ZenButton>
        <NextButton
          onClick={handleNext}
          disabled={selectedParts.length === 0}
        >
          Continue — Manual Mode
        </NextButton>
      </ButtonContainer>
      <HelpButton
        title="Create Your Meditation - Part Selection"
        content={helpContent}
        position="fixed-bottom-right"
        />
    </Container>


  );
};

export default Plan; 